import { createContext, useContext, type ReactNode } from "react";
import { matchDetailRoute, PUBLIC_PAGES } from "@/lib/seo/meta";

export const PUBLIC_SSR_QUERY_KEY = ["aero-public-ssr-snapshot"] as const;
const PublicSsrPath = createContext<string | null>(null);

/** Presentation only: never establishes an identity or authorizes an API call. */
export function isPublicSsrPath(path: string): boolean {
  if (!path.startsWith("/") || /[?#\\]/.test(path) || path.startsWith("//"))
    return false;
  try {
    return (
      Object.hasOwn(PUBLIC_PAGES, path) ||
      matchDetailRoute(path) !== null ||
      /^\/(?:fuel-prices|avgas-prices)(?:\/[a-z-]+)?$/.test(path)
    );
  } catch {
    return false;
  }
}

/** Accept a snapshot only for hydration of the exact server-rendered public route. */
export function publicSsrHydrationPath(
  state: unknown,
  pathname: string,
): string | null {
  if (
    !isPublicSsrPath(pathname) ||
    !state ||
    typeof state !== "object" ||
    !("queries" in state) ||
    !Array.isArray(state.queries)
  )
    return null;
  const snapshot = state.queries.find((query: unknown) => {
    if (!query || typeof query !== "object" || !("queryKey" in query))
      return false;
    return (
      Array.isArray(query.queryKey) &&
      query.queryKey.length === 1 &&
      query.queryKey[0] === PUBLIC_SSR_QUERY_KEY[0]
    );
  });
  return snapshot?.state?.status === "success" &&
    snapshot.state.data?.pathname === pathname
    ? pathname
    : null;
}

export function PublicSsrBoundary({
  pathname,
  children,
}: {
  pathname: string | null;
  children: ReactNode;
}) {
  return (
    <PublicSsrPath.Provider
      value={pathname && isPublicSsrPath(pathname) ? pathname : null}
    >
      {children}
    </PublicSsrPath.Provider>
  );
}

export const usePublicSsrPath = () => useContext(PublicSsrPath);
